import React, { Fragment, useRef, useState, useEffect } from "react";
import COLORS from "../../../assets/Colors";
import CustomTable from "../../common/CustomTable/CustomTable";
import ActionBar from "../../common/ActionBar";
import _ from "lodash";
import queryString from "query-string";
import { useSnackbar } from "notistack";
import { getErrorMessage } from "../../../helpers/functions";
import styled from "styled-components";
import { navigate } from "@reach/router";
import moment from "moment";
import { Bold } from "../../internal/RecordingsPage";
import BookingService from "../../../services/BookingService";
import { downloadInvoice } from "../data/mockBookings";
import { DialogTitle, DialogContent, DialogActions } from "@material-ui/core";
import Button from "../../booking_dashboard/components/Button";
import { CustomInputField } from "../../common/FormInputs";
import {
  StyledDialog,
  ModalTitle,
  ModalAmountBanner,
  ModalAmountRow,
  ModalSectionLabel,
  ModalHelperText,
} from "../../booking_dashboard/Bookings/BookingDetail";

const initFilters = [
  { label: "All", key: "allCount", value: 0 },
  { label: "Booking", key: "bookingCount", value: 0 },
  { label: "On-Site", key: "onSiteCount", value: 0 },
  { label: "Refund", key: "refundCount", value: 0 },
];

const filterByStatus = (selectedFilter) => {
  let key = "";
  switch (selectedFilter) {
    case "All":
      key = "";
      break;
    case "Booking":
      key = "booking";
      break;
    case "On-Site":
      key = "onSite";
      break;
    case "Refund":
      key = "refund";
      break;
    default:
      break;
  }
  return key;
};

const fmtINR = (amount) => `₹${Number(amount || 0).toLocaleString("en-IN")}`;

async function getInvoicesList(query = "", status) {
  try {
    const response = await BookingService.fetchInvoice(query);
    let stats = {
      onSiteCount: status === "onSite" ? response?.data?.total : 0,
      bookingCount: status === "booking" ? response?.data?.total : 0,
      refundCount: status === "refund" ? response?.data?.total : 0,
      allCount:
        status !== "refund" && status !== "booking" && status !== "onSite"
          ? response?.data?.total
          : 0,
    };
    return { ...response?.data, stats };
  } catch (error) {
    throw error;
  }
}

const InvoicesPage = () => {
  const breadcrumbs = ["Booking Dashboard", "All Invoices"];
  const [state, setState] = useState({});
  const MTRef = useRef();
  const { enqueueSnackbar } = useSnackbar();
  const [filters, setFilters] = useState(initFilters);
  const [selectedFilter, setSelectedFilter] = useState();
  const [confirmRefundModal, setConfirmRefundModal] = useState({
    open: false,
    bookingId: null,
    invoiceId: null,
    refundAmount: 0,
    transactionId: "",
  });

  useEffect(() => {
    if (selectedFilter && MTRef?.current?.onQueryChange) {
      MTRef.current.onQueryChange({
        ...MTRef.current.state.query,
        page: 0,
      });
    }
  }, [selectedFilter]);

  const onBreadcrumbClick = (selected) => {
    switch (_.snakeCase(selected)) {
      case "booking_dashboard":
        navigate("/dashboard/case-manager/booking-dashboard");
        break;
      default:
        break;
    }
  };

  function setStats(stats) {
    const mapped = initFilters.map((filter) => {
      let returnData = { label: filter.label };
      if (stats[filter.key] >= 0) {
        returnData.value = stats[filter.key];
      }
      return returnData;
    });
    setFilters(mapped);
  }

  const openConfirmRefundModal = (rowData) => {
    setConfirmRefundModal({
      open: true,
      bookingId: rowData?.bookingId ?? rowData?.booking?.id ?? null,
      invoiceId: rowData?.id ?? null,
      refundAmount: Math.abs(rowData?.totalAmount || rowData?.amount || 0),
      transactionId: "",
    });
  };

  const closeConfirmRefundModal = () => {
    setConfirmRefundModal((prev) => ({ ...prev, open: false }));
  };

  const handleConfirmRefund = async () => {
    if (!confirmRefundModal.transactionId) {
      enqueueSnackbar("Please enter the bank transaction ID", {
        variant: "warning",
      });
      return;
    }
    try {
      const res = await BookingService.confirmRescheduleRefund({
        booking_id: confirmRefundModal.bookingId,
        // invoiceId: confirmRefundModal.invoiceId,
        transaction_id: confirmRefundModal.transactionId,
      });

      if (res?.refund_invoice_url) {
        downloadInvoice(res.refund_invoice_url);
      }

      enqueueSnackbar("Refund confirmed successfully", {
        variant: "success",
      });
      if (MTRef?.current) MTRef.current.onQueryChange("");
      closeConfirmRefundModal();
    } catch (error) {
      enqueueSnackbar(getErrorMessage(error), { variant: "error" });
    }
  };

  const columns = [
    {
      field: "invoiceNumber",
      title: "INVOICE NO.",
      sorting: true,
      render: (rowData) => (
        <HyperLink>{rowData.invoiceNumber || "-"}</HyperLink>
      ),
    },
    {
      field: "bookingReference",
      title: "BOOKING REF",
      sorting: false,
      render: (rowData) => <Bold>{rowData.bookingReference || "-"}</Bold>,
    },
    {
      field: "client",
      title: "CLIENT",
      sorting: false,
      render: (rowData) => rowData.client?.name || "-",
    },
    {
      field: "invoiceDate",
      title: "DATE",
      sorting: true,
      render: (rowData) =>
        rowData.invoiceDate
          ? moment(rowData.invoiceDate).format("DD/MM/YYYY")
          : "-",
    },
    {
      field: "gstAmount",
      title: "AMOUNT",
      sorting: false,
      render: (rowData) => (
        <div style={{ fontWeight: 600, fontSize: "16px" }}>
          {fmtINR(rowData.totalAmount)}
        </div>
      ),
    },
    {
      field: "paymentMode",
      title: "MODE",
      sorting: false,
      render: (rowData) => rowData.paymentMode || "-",
    },
    {
      field: "paymentStatus",
      title: "STATUS",
      sorting: false,
      render: (rowData) => {
        const isPaid = rowData.paymentStatus === "paid";
        return (
          <div
            style={{
              background: isPaid ? "#E8F5E9" : "#FFF3E0",
              color: isPaid ? "#00C853" : "#F57C00",
              fontSize: "13px",
              fontWeight: 700,
              padding: "4px 14px",
              borderRadius: "20px",
              display: "inline-block",
              textTransform: "uppercase",
            }}
          >
            {isPaid ? "PAID" : "PENDING"}
          </div>
        );
      },
      headerStyle: { paddingLeft: "20px" },
    },
    {
      field: "invoiceType",
      title: "TYPE",
      sorting: false,
      render: (rowData) => {
        const type = rowData.invoiceType?.toLowerCase();
        let bg = "#E3F2FD";
        let color = "#1976D2";

        if (type === "booking") {
          bg = "#E8F5E9";
          color = "#2E7D32";
        } else if (type === "on-site" || type === "supplementary") {
          bg = "#FFF3E0";
          color = "#F57C00";
        } else if (type === "refund") {
          bg = "#FFEBEE";
          color = "#C62828";
        }

        return (
          <div
            style={{
              background: bg,
              color,
              fontSize: "12.5px",
              fontWeight: 700,
              padding: "4px 12px",
              borderRadius: "20px",
              display: "inline-block",
              textTransform: "capitalize",
            }}
          >
            {rowData.invoiceType || "-"}
          </div>
        );
      },
      headerStyle: { paddingLeft: "30px" },
    },
    {
      field: "invoiceUrl",
      title: "",
      sorting: false,
      render: (rowData) => (
        <HyperLink
          onClick={() => downloadInvoice(rowData.invoiceUrl)}
          style={{ color: COLORS.BTN_GREEN, fontWeight: 600 }}
        >
          PDF
        </HyperLink>
      ),
    },
    {
      field: "refundAction",
      title: "",
      sorting: false,
      render: (rowData) => {
        const isPending = rowData.paymentStatus !== "paid";
        if (!isPending) return null;
        return (
          <HyperLink
            onClick={() => openConfirmRefundModal(rowData)}
            style={{ color: COLORS.BTN_GREEN, fontWeight: 600 }}
          >
            REFUND
          </HyperLink>
        );
      },
    },
  ];

  const data = (query) =>
    new Promise((resolve) => {
      let params = {
        page: query.page + 1,
        perPage: 10,
        invoice_type: filterByStatus(selectedFilter),
      };

      if (query.orderBy?.field) {
        params.sort = query.orderBy.field;
        params.sortDirection = query.orderDirection;
      }

      if (query?.search) {
        params.search = query.search;
      }

      let stringParams = "";
      if (!_.isEmpty(params)) {
        stringParams = `?${queryString.stringify(params)}`;
      }

      getInvoicesList(stringParams, params.invoice_type)
        .then((result) => {
          resolve({
            data: result.data,
            page: result.page - 1,
            total: result.total,
          });
          setState(result);
          setStats(result.stats);
        })
        .catch((error) => {
          enqueueSnackbar(getErrorMessage(error), { variant: "error" });
          resolve({ data: [], page: 0, total: 0 });
        });
    });

  return (
    <Fragment>
      <ActionBar {...{ breadcrumbs, onBreadcrumbClick }} />
      <Padding>
        <CustomTable
          hidePagination={state.lastPage === 1}
          pageSize={state?.data?.length || 10}
          pluralTitle="Invoices"
          singularTitle="Invoice"
          hideFilterBar={false}
          placeholderText="Search Invoices..."
          state={state}
          {...{
            columns,
            data,
            MTRef,
            filters,
            selectedFilter,
            setSelectedFilter,
          }}
        />
      </Padding>

      {confirmRefundModal.open && (
        <StyledDialog
          open={confirmRefundModal.open}
          onClose={closeConfirmRefundModal}
          keepMounted={false}
          disableRestoreFocus
        >
          <DialogTitle>
            <ModalTitle>Confirm Refund</ModalTitle>
          </DialogTitle>
          <DialogContent>
            <ModalAmountBanner>
              <ModalAmountRow $total>
                <span>Refund Amount</span>
                <strong>{fmtINR(confirmRefundModal.refundAmount)}</strong>
              </ModalAmountRow>
            </ModalAmountBanner>

            <ModalSectionLabel>
              Bank Transaction ID (NEFT Ref)
            </ModalSectionLabel>
            <CustomInputField
              fullWidth
              variant="outlined"
              size="small"
              placeholder="e.g. NEFT-REF-88213"
              value={confirmRefundModal.transactionId}
              onChange={(e) => {
                const value = e?.target?.value ?? "";
                setConfirmRefundModal((prev) => ({
                  ...prev,
                  transactionId: value,
                }));
              }}
            />
            <ModalHelperText>
              Enter this only after the bank transfer has actually been
              completed. This confirms the refund and generates the credit note
              invoice.
            </ModalHelperText>
          </DialogContent>
          <DialogActions>
            <Button
              variant="secondary"
              size="md"
              onClick={closeConfirmRefundModal}
            >
              Do This Later
            </Button>
            <Button variant="primary" size="md" onClick={handleConfirmRefund}>
              Confirm Refund & Download Invoice
            </Button>
          </DialogActions>
        </StyledDialog>
      )}
    </Fragment>
  );
};

export default InvoicesPage;

const Padding = styled.div`
  padding-top: 30px;
  padding-bottom: 50px;
  padding-left: 35px;
  padding-right: 53px;
`;

const HyperLink = styled.span`
  cursor: pointer;
  color: ${COLORS.BTN_GREEN};
  text-decoration: underline;
`;
